import { useState } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import {
  MAX_FILENAME_LENGTH,
  MAX_PDF_SIZE,
  UPLOAD_ERROR_COMPONENTS,
  reportUploadError,
  useUpload,
} from '@/hooks/useUpload'
import { SubmitHandler, useForm } from 'react-hook-form'
import { BiListUl } from 'react-icons/bi'
import { HiOutlineCheckCircle } from 'react-icons/hi'
import { useDebounce } from 'use-debounce'

import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
} from '@chakra-ui/react'

import {
  Button,
  FormFileUploadControl,
  FormInputControl,
  FormInputSelectControl,
} from '@/components/ui'
import {
  useCreateStandardOperatingProcedureMutation,
  useFacilityNamesQuery,
} from '@/graphql/generated/hooks'
import { SopType, UploadedFileType } from '@/graphql/generated/schemas'
import { OptionI, getFacilityOptions } from '@/utils/forms/optionBuilders'
import {
  MIN_CHAR_COUNT,
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

import { useFacilitySelectAll } from '../hooks'
import { SOP_ROLES } from '../utils/utils'
import { FacilitySelectHeader } from './FacilitySelectHeader'

interface CreateSOPModalIProps {
  isOpen: boolean
  onClose: () => void
}

interface FormInputsIProps {
  title: string
  facilities?: OptionI[]
  role: OptionI<SopType>
  file: FileList
}

const PDF_MIME_TYPE = 'application/pdf'

export const CreateSOPModal = ({ isOpen, onClose }: CreateSOPModalIProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )
  const { shouldEnableCreateSOP } = usePermissions()
  const { showSuccess, showError } = useToasts()

  const [upload] = useUpload()
  const [createStandardOperatingProcedure] =
    useCreateStandardOperatingProcedureMutation()
  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityNamesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter:
          debouncedfacilityName?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilityName }
            : undefined,
      },
    })
  const {
    handleSubmit,
    register,
    control,
    errors,
    formState,
    trigger,
    watch,
    setValue,
  } = useForm<FormInputsIProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { isValid } = formState

  const facilityOptions = getFacilityOptions(facilitiesData)

  const { areAllFacilitiesSelected, handleSelectAllFacilities } =
    useFacilitySelectAll({
      watch,
      setValue,
      facilityOptions,
    })

  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    setIsLoading(true)

    const file = values.file.item(0)
    const sopTitle = values.title
    const facilityIds = values.facilities?.map((f) => f.value) || []

    try {
      const fileId = await upload({
        file,
        fileType: UploadedFileType.StandardOperatingProcedure,
        validationOptions: {
          maxFileSizeInBytes: MAX_PDF_SIZE,
          maxFileNameLength: MAX_FILENAME_LENGTH,
        },
      })

      try {
        const { data } = await createStandardOperatingProcedure({
          variables: {
            input: {
              name: sopTitle,
              externalId: fileId,
              facilityIds: facilityIds,
              type: values.role.value,
            },
          },
          refetchQueries: ['StandardOperatingProcedures'],
        })
        const sopReturnedTitle =
          data?.createStandardOperatingProcedure?.standardOperatingProcedure
            ?.name
        showSuccess(`Successfully added ${sopReturnedTitle}`)
        setIsLoading(false)
        onClose()
      } catch (error) {
        // Error creating SOP mutation
        showError(error)
        setIsLoading(false)
      }
    } catch (error) {
      reportUploadError(error, UPLOAD_ERROR_COMPONENTS.CreateSOPModal)
      showError(error.message)
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff' rounded='20px'>
        <ModalHeader>
          <Box align-items='center' display='flex' m='20px auto'>
            <Box display='grid' placeItems='center'>
              <BiListUl size='32px' />
            </Box>
            <Box
              color='#353849'
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              ml={2}
            >
              Add SOP
            </Box>
            <Spacer />
            <ModalCloseButton color='#000' opacity={0.4} position='initial' />
          </Box>
        </ModalHeader>
        <ModalBody p={0}>
          <Box px={6} py={2}>
            <Box mb={3}>
              <FormInputControl
                errorMessage={errors.title && errors.title.message}
                id='title'
                inputRef={register({
                  required: 'SOP title is required',
                })}
                isDisabled={isLoading}
                isInvalid={!!errors.title}
                label='SOP Title'
                placeholder='Enter a title for this SOP'
              />
            </Box>
            <Box mb={3}>
              <FacilitySelectHeader
                allFacilitiesSelected={areAllFacilitiesSelected}
                dataTestId='CreateSOPModal:selectAllFacilities'
                isDisabled={isFacilitiesLoading || facilityOptions.length === 0}
                onToggleSelectAll={handleSelectAllFacilities}
              />
              <FormInputSelectControl
                closeMenuOnSelect={false}
                control={control}
                data-testid='CreateSOPModal:facilities'
                defaultValue={null}
                id='facilities'
                isClearable
                isInvalid={!!errors.facilities}
                isLoading={isFacilitiesLoading}
                isMulti
                isSearchable
                noOptionsMessage={({ inputValue }) =>
                  getFacilityNoOptionsMessage(inputValue)
                }
                onInputChange={setFacilityName}
                options={facilityOptions}
                placeholder='Add facilities where this SOP applies'
              />
            </Box>
            <Box mb={3}>
              <FormInputSelectControl
                control={control}
                data-testid='CreateSOPModal:role'
                defaultValue={null}
                errorMessage={errors.role && 'Select a role'}
                id='role'
                isDisabled={isLoading}
                isInvalid={!!errors.role}
                label='Available To'
                options={SOP_ROLES}
                placeholder='Select the role that will have access to the SOP'
                rules={{ required: true }}
              />
            </Box>
            <Box mb={3}>
              <FormFileUploadControl
                accept='.pdf'
                errorMessage={errors.file && errors.file.message}
                id='file'
                inputRef={register({
                  required: 'SOP PDF is required',
                  validate: (fl: FileList) =>
                    fl?.[0]?.type === PDF_MIME_TYPE ||
                    'File should be in .pdf format',
                })}
                isDisabled={isLoading}
                isInvalid={!!errors.file}
                label='SOP PDF'
                onFileRemoved={() => trigger(['file'])}
                placeholder='Attach PDF'
              />
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter pb={6} py={4}>
          <Button mr={3} onClick={onClose} variant='secondary'>
            Cancel
          </Button>
          <Button
            isDisabled={!shouldEnableCreateSOP || !isValid}
            isLoading={isLoading}
            leftIcon={<HiOutlineCheckCircle size='24px' />}
            loadingText='Uploading SOP'
            onClick={handleSubmit(onSubmit)}
          >
            Upload SOP
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
